/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// our components
import { promotedAdPackagesStatus } from 'config';
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import { PROMOTED_AD_PACKAGES } from 'routes/paths';
import PromotedAdPackagesList from './List';

/**
 * Query to fetch packages from api
 */
export const PROMOTED_AD_PACKAGES_QUERY = gql`
  query PromotedAdPackagesList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $id: ID
    $ad: ID
    $locationScope: [FilterPromotedAdPackageLocationScope]
    $subcategory: ID
    $category: ID
    $province: ID
    $municipality: ID
    $status: [FilterPromotedAdPackageStatus]
    $duration: [RelativeDateTimeInput]
  ) {
    promotedAdPackages(
      first: $first
      after: $after
      last: $last
      before: $before
      id: $id
      ad: $ad
      locationScope: $locationScope
      subcategory: $subcategory
      category: $category
      province: $province
      municipality: $municipality
      status: $status
      duration: $duration
    ) {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          price
          status
          updatedOn
          duration {
            years
            months
            days
            hours
            minutes
            seconds
          }
          subcategory {
            parentCategory {
              title
            }
            title
          }
          province {
            name
          }
          municipality {
            name
          }
          locationScope
          name
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to update package
 */
export const UPDATE_PACKAGE_MUTATION = gql`
  mutation UpdatePromotedAdPackages($ids: [ID]!, $price: Float!) {
    updatePromotedAdPackage(input: { ids: $ids, price: $price }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to disable package
 */
export const DISABLE_PACKAGE_MUTATION = gql`
  mutation DeactivatePromotedAdPackages($ids: [ID]!) {
    deactivatePromotedAdPackage(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Default filters in Package List
 */
export const defaultFilters = {
  status: [promotedAdPackagesStatus.ENABLED],
  category: undefined,
  subcategory: undefined,
  province: undefined,
  ad: undefined,
};

/**
 * @class
 * @extends Component
 */
class PromotedAdPackagesListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const {
      initialFilters: { count, ...initialFilters },
    } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected: parseInt(numberOfRowsSelected, 0) || 25,
      filters: {
        ...defaultFilters,
        ...initFilters,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
      itemProcessing: [],
      failedIds: [],
      notification: count
        ? { message: `Se crearon ${count} paquetes`, type: 'success' }
        : {},
    };
    this.Compose = adopt({
      mutatePackageUpdate: this.packageUpdate,
      mutatePackageDisable: this.packageDisable,
      query: this.listPackages,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Mutation update callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  packageUpdate = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={UPDATE_PACKAGE_MUTATION}
        onCompleted={this.mutationCompleted(
          'updatePromotedAdPackage',
          itemProcessing,
        )}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {handlePackageUpdate => render({ handlePackageUpdate })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation disable callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  packageDisable = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={DISABLE_PACKAGE_MUTATION}
        onCompleted={this.mutationCompleted(
          'deactivatePromotedAdPackage',
          itemProcessing,
        )}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {handlePackageDisable => render({ handlePackageDisable })}
      </Mutation>
    );
  };

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listPackages = ({ render }) => {
    const { numberOfRowsSelected, filters, pagination } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };

    return (
      <Query
        query={PROMOTED_AD_PACKAGES_QUERY}
        variables={{
          ...filters,
          duration: filters.duration && [filters.duration],
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const {
      pageInfo,
      edges: newEdges,
    } = options.fetchMoreResult.promotedAdPackages;
    if (newEdges.length !== 0) {
      return {
        promotedAdPackages: {
          __typename: previousResult.promotedAdPackages.__typename,
          edges: [...previousResult.promotedAdPackages.edges, ...newEdges],
          pageInfo: {
            ...previousResult.promotedAdPackages.pageInfo,
            hasNextPage: pageInfo.hasNextPage,
          },
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * Handle change order.
   *
   * @param {Object} newOrder - Object that contain order mapping.
   */
  handleChangeOrder = newOrder => {
    const history = this.context;
    const {
      appliedOrder: { orderField, orderAsc },
    } = this.state;
    const appliedOrder = {
      orderField: newOrder.val,
      orderAsc: !(orderField === newOrder.val && orderAsc),
      orderColumn: newOrder.column,
    };
    this.setState({ appliedOrder });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({ ...parsed, appliedOrder });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * OnComplete delete mutation.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const { t } = this.props;
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );
    this.setState({
      itemProcessing: [],
      failedIds,
      notification:
        failedIds.length > 0
          ? {
              type: 'failed',
              message: t(`common.failed_bulk_action`, {
                count: failedIds.length,
              }),
            }
          : {},
    });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters, ...filters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Redirect to package/create route.
   */
  handleCreatePackage = () => {
    const history = this.context;
    const parsed = getQueryString(history);

    const locationScope =
      parsed.locationScope &&
      (typeof parsed.locationScope === 'string'
        ? parsed.locationScope
        : parsed.locationScope[0]);

    const stringified = qs.stringify({
      category: parsed.category,
      subcategory: parsed.subcategory,
      province: parsed.province,
      municipality: parsed.municipality,
      locationScope,
      duration: parsed.duration,
    });
    history.push({
      pathname: PROMOTED_AD_PACKAGES.CREATE,
      search: stringified,
    });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    const {
      notification: { type },
    } = this.state;
    this.setState({ notification: { type } });

    const history = this.context;
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    delete parsed.count;
    const stringified = qs.stringify(parsed);
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Render AdsList component.
   */
  render() {
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      pagination,
      failedIds,
      notification,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutatePackageUpdate: { handlePackageUpdate },
          mutatePackageDisable: { handlePackageDisable },
          query: { loading, data },
        }) => (
          <PromotedAdPackagesList
            data={data}
            changeOrder={this.handleChangeOrder}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            packageUpdate={(items, price) => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handlePackageUpdate({ variables: { ids: items, price } });
            }}
            packageDisable={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handlePackageDisable({ variables: { ids: items } });
            }}
            refresh={this.handleRefresh}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            restartFilters={this.handleRestartFilters}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            notification={notification}
            closeNotification={this.handleCloseNotification}
            createPackagePage={this.handleCreatePackage}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * PromotedAdPackagesListContainer PropTypes
 * @param t - prop from HOC with string translations
 * @param initialFilters - prop initial Filters to apply
 */

PromotedAdPackagesListContainer.propTypes = {
  t: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({}).isRequired,
};

export default withTranslation()(PromotedAdPackagesListContainer);
